import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { showToast } from "../../helper/toastHelper";
import { CallAPI } from "../../utils/apiActions";
import {
  BOT_PROCESSING_PROGRESS,
  KNOWLEDGE_SOURCE_DOCUMENT,
} from "../../utils/APIEndPoints";
import {
  BOT_PROCESSING_DELETED_EVENT,
  BOT_PROCESSING_PROGRESS_EVENT,
  emitBotProcessingCompleted,
  emitBotProcessingDeleted,
} from "../../utils/botProcessingProgress";
import { LABEL } from "../../utils/constant";

const STATUS = {
  PROCESSING: "processing",
  COMPLETED: "completed",
  FAILED: "failed",
};

const clampProgress = (value) => {
  const parsedValue = Number(value);

  if (Number.isNaN(parsedValue)) {
    return 0;
  }

  return Math.min(Math.max(parsedValue, 0), 100);
};

const getOverallProgress = (knowledgeSources = [], fallback = 0) => {
  if (!Array.isArray(knowledgeSources) || knowledgeSources.length === 0) {
    return clampProgress(fallback);
  }

  const totalProgress = knowledgeSources.reduce(
    (progressTotal, source) => progressTotal + clampProgress(source?.progress),
    0,
  );

  return clampProgress(Math.round(totalProgress / knowledgeSources.length));
};

export default function BotProcessingProgress({ botId }) {
  const [progressData, setProgressData] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [shouldTrack, setShouldTrack] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [pendingActionBySource, setPendingActionBySource] = useState({});
  const deletedSourceIdsRef = useRef(new Set());
  const previousStatusBySourceRef = useRef({});

  const removeDeletedKnowledgeSources = useCallback(
    (knowledgeSourceIds = []) => {
      const normalizedSourceIds = knowledgeSourceIds
        .filter(Boolean)
        .map((sourceId) => `${sourceId}`);

      if (normalizedSourceIds.length === 0) {
        return;
      }

      normalizedSourceIds.forEach((sourceId) => {
        deletedSourceIdsRef.current.add(sourceId);
        delete previousStatusBySourceRef.current[sourceId];
      });

      setPendingActionBySource((prev) => {
        const nextState = { ...prev };

        normalizedSourceIds.forEach((sourceId) => {
          delete nextState[sourceId];
        });

        return nextState;
      });

      setProgressData((prev) => {
        if (!prev || !Array.isArray(prev.knowledge_sources)) {
          return prev;
        }

        const nextKnowledgeSources = prev.knowledge_sources.filter(
          (source) => !deletedSourceIdsRef.current.has(`${source?.ks_id}`),
        );

        if (nextKnowledgeSources.length === prev.knowledge_sources.length) {
          return prev;
        }

        return {
          ...prev,
          overall_progress: getOverallProgress(nextKnowledgeSources),
          knowledge_sources: nextKnowledgeSources,
        };
      });
    },
    [],
  );

  const knowledgeSources = useMemo(() => {
    if (!Array.isArray(progressData?.knowledge_sources)) {
      return [];
    }

    return progressData.knowledge_sources;
  }, [progressData]);

  const hasProcessing = useMemo(
    () =>
      knowledgeSources.some(
        ({ status }) => `${status}`.toLowerCase() === STATUS.PROCESSING,
      ),
    [knowledgeSources],
  );

  const allCompleted = useMemo(
    () =>
      knowledgeSources.length > 0 &&
      knowledgeSources.every(
        ({ status }) => `${status}`.toLowerCase() === STATUS.COMPLETED,
      ),
    [knowledgeSources],
  );

  const hasFailed = useMemo(
    () =>
      knowledgeSources.some(
        ({ status }) => `${status}`.toLowerCase() === STATUS.FAILED,
      ),
    [knowledgeSources],
  );

  const canClose = allCompleted && !hasFailed;

  const fetchProgress = useCallback(
    async (force = false) => {
      if (!botId || (!shouldTrack && !force)) {
        setProgressData(null);
        setIsVisible(false);
        return;
      }

      const response = await CallAPI(
        "CHAT",
        "GET",
        `${BOT_PROCESSING_PROGRESS}?bot_id=${botId}`,
        "",
        false,
        false,
      );

      console.log(response);
      // !response?.status ||
      if (!response?.res?.data && response?.res?.status != 200) {
        return;
      }

      const nextProgressData = response.res.data;
      const rawKnowledgeSources = Array.isArray(
        nextProgressData.knowledge_sources,
      )
        ? nextProgressData.knowledge_sources
        : [];
      const nextKnowledgeSources = rawKnowledgeSources.filter(
        (source) => !deletedSourceIdsRef.current.has(`${source?.ks_id}`),
      );
      const filteredProgressData =
        nextKnowledgeSources.length === rawKnowledgeSources.length
          ? nextProgressData
          : {
              ...nextProgressData,
              overall_progress: getOverallProgress(nextKnowledgeSources),
              knowledge_sources: nextKnowledgeSources,
            };
      const nextStatusBySource = {};
      const completedSourceIds = [];
      const nextHasProcessing = nextKnowledgeSources.some(
        ({ status }) => `${status}`.toLowerCase() === STATUS.PROCESSING,
      );
      const nextHasFailed = nextKnowledgeSources.some(
        ({ status }) => `${status}`.toLowerCase() === STATUS.FAILED,
      );

      nextKnowledgeSources.forEach((source, index) => {
        const sourceKey = source?.ks_id ?? `source-${index}`;
        const normalizedStatus = `${source?.status || ""}`.toLowerCase();

        nextStatusBySource[sourceKey] = normalizedStatus;

        if (
          normalizedStatus === STATUS.COMPLETED &&
          previousStatusBySourceRef.current[sourceKey] !== STATUS.COMPLETED
        ) {
          completedSourceIds.push(source?.ks_id);
        }
      });

      previousStatusBySourceRef.current = nextStatusBySource;

      setProgressData(filteredProgressData);

      if (nextKnowledgeSources.length === 0) {
        previousStatusBySourceRef.current = {};
        setIsVisible(false);
        setIsDismissed(false);
        setShouldTrack(false);
        return;
      }

      const uniqueCompletedSourceIds = completedSourceIds.filter(Boolean);

      if (uniqueCompletedSourceIds.length > 0) {
        emitBotProcessingCompleted(botId, uniqueCompletedSourceIds);
      }

      if (nextHasProcessing || nextHasFailed) {
        setIsDismissed(false);
        setShouldTrack(true);
        setIsVisible(true);
        return;
      }

      if (force && !shouldTrack) {
        setIsVisible(false);
        return;
      }

      setIsVisible(!isDismissed);
    },
    [botId, isDismissed, shouldTrack],
  );

  function truncateText(value = "", maxLength = 50) {
    if (value.length <= maxLength) return value;
    return `${value.slice(0, maxLength)}...`;
  }

  const handleSourceAction = useCallback(
    async (method, ksId, actionLabel) => {
      if (!botId || !ksId) {
        return;
      }

      setPendingActionBySource((prev) => ({
        ...prev,
        [ksId]: method,
      }));

      if (method === "DELETE") {
        await CallAPI("USER", "DELETE", `${KNOWLEDGE_SOURCE_DOCUMENT}/${ksId}`);
      }

      const response = await CallAPI(
        "CHAT",
        method,
        BOT_PROCESSING_PROGRESS,
        {
          bot_id: botId,
          ks_id: ksId,
        },
        false,
        false,
      );

      showToast(
        response.msg || `${actionLabel} request submitted successfully.`,
        LABEL.TOAST_TYPE.SUCCESS,
      );

      if (method === "POST") {
        setIsDismissed(false);
        setIsVisible(true);
        setIsMinimized(false);
        setShouldTrack(true);
      }

      if (method === "DELETE") {
        removeDeletedKnowledgeSources([ksId]);
        emitBotProcessingDeleted(botId, [ksId]);
      }

      await fetchProgress(true);

      // if (response?.res?.status == 200) {
      //   showToast(
      //     response.msg || `${actionLabel} request submitted successfully.`,
      //     LABEL.TOAST_TYPE.SUCCESS,
      //   );

      //   if (method === "POST") {
      //     setIsDismissed(false);
      //     setIsVisible(true);
      //     setIsMinimized(false);
      //     setShouldTrack(true);
      //   }

      //   if (method === "DELETE") {
      //     removeDeletedKnowledgeSources([ksId]);
      //   }

      //   await fetchProgress(true);
      // } else {
      //   showToast(
      //     response?.msg || `Unable to ${actionLabel.toLowerCase()} right now.`,
      //     LABEL.TOAST_TYPE.ERROR,
      //   );
      // }

      setPendingActionBySource((prev) => {
        const nextState = { ...prev };
        delete nextState[ksId];
        return nextState;
      });
    },
    [botId, fetchProgress, removeDeletedKnowledgeSources],
  );

  useEffect(() => {
    deletedSourceIdsRef.current = new Set();
    previousStatusBySourceRef.current = {};
  }, [botId]);

  useEffect(() => {
    if (!botId) {
      return undefined;
    }

    const handleStartTracking = (event) => {
      if (event?.detail?.botId && event.detail.botId !== botId) {
        return;
      }

      setIsDismissed(false);
      setIsMinimized(false);
      setShouldTrack(true);
    };

    window.addEventListener(BOT_PROCESSING_PROGRESS_EVENT, handleStartTracking);

    return () => {
      window.removeEventListener(
        BOT_PROCESSING_PROGRESS_EVENT,
        handleStartTracking,
      );
    };
  }, [botId]);

  useEffect(() => {
    if (!botId) {
      return undefined;
    }

    const handleDeletedSources = (event) => {
      if (event?.detail?.botId && event.detail.botId !== botId) {
        return;
      }

      removeDeletedKnowledgeSources(event?.detail?.knowledgeSourceIds);
    };

    window.addEventListener(BOT_PROCESSING_DELETED_EVENT, handleDeletedSources);

    return () => {
      window.removeEventListener(
        BOT_PROCESSING_DELETED_EVENT,
        handleDeletedSources,
      );
    };
  }, [botId, removeDeletedKnowledgeSources]);

  useEffect(() => {
    fetchProgress(true);
  }, [fetchProgress]);

  useEffect(() => {
    if (!progressData || knowledgeSources.length > 0) {
      return;
    }

    previousStatusBySourceRef.current = {};
    setIsVisible(false);
    setIsDismissed(false);
    setShouldTrack(false);
    setIsMinimized(false);
  }, [knowledgeSources.length, progressData]);

  useEffect(() => {
    if (!hasProcessing && !hasFailed && isMinimized) {
      setIsMinimized(false);
    }
  }, [hasFailed, hasProcessing, isMinimized]);

  useEffect(() => {
    if (shouldTrack) {
      fetchProgress();
    }
  }, [fetchProgress, shouldTrack]);

  useEffect(() => {
    if (!botId || !shouldTrack) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      fetchProgress();
    }, 10000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [botId, fetchProgress, shouldTrack]);

  if (!botId || !progressData || !isVisible || knowledgeSources.length === 0) {
    return null;
  }

  return (
    <aside
      className={`bot-progress-widget ${isMinimized ? "is-minimized" : ""}`}
      aria-live="polite"
    >
      <div className="bot-progress-widget__header">
        <div>
          <span className="bot-progress-widget__eyebrow">
            Bot processing progress
          </span>
          <h4>
            {hasProcessing
              ? "Knowledge sources are processing"
              : hasFailed
                ? "Processing finished with issues"
                : "Knowledge sources are ready"}
          </h4>
        </div>

        <div className="bot-progress-widget__actions">
          {(hasProcessing || hasFailed) && (
            <button
              type="button"
              className="bot-progress-widget__toggle"
              aria-label={
                isMinimized
                  ? "Expand progress notification"
                  : "Minimize progress notification"
              }
              onClick={() => setIsMinimized((prev) => !prev)}
            >
              {isMinimized ? "+" : "-"}
            </button>
          )}

          {canClose && (
            <button
              type="button"
              className="bot-progress-widget__close"
              aria-label="Close progress notification"
              onClick={() => {
                setIsDismissed(true);
                setIsVisible(false);
                setShouldTrack(false);
                setIsMinimized(false);
              }}
            >
              x
            </button>
          )}
        </div>
      </div>

      <div className="bot-progress-widget__summary">
        <div className="bot-progress-widget__summary-row">
          <span>Overall progress</span>
          <strong>{clampProgress(progressData?.overall_progress)}%</strong>
        </div>
        <div className="bot-progress-widget__bar">
          <span
            style={{
              width: `${clampProgress(progressData?.overall_progress)}%`,
            }}
          />
        </div>
      </div>

      {!isMinimized && (
        <>
          <div className="bot-progress-widget__list">
            {knowledgeSources.map((source) => {
              const progress = clampProgress(source?.progress);
              const status = `${source?.status || ""}`.toLowerCase();
              const isFailed = status === STATUS.FAILED;
              const pendingAction = pendingActionBySource[source?.ks_id];
              const isActionPending = Boolean(pendingAction);

              return (
                <div className="bot-progress-widget__item" key={source?.ks_id}>
                  <div className="bot-progress-widget__item-head">
                    <div>
                      <h5>
                        {truncateText(
                          source?.ks_name || "Knowledge source",
                          35,
                        )}
                      </h5>
                      <p>{source?.process_type || "source"}</p>
                    </div>
                    <span
                      className={`bot-progress-widget__status ${status || ""}`}
                    >
                      {status || "-"}
                    </span>
                  </div>

                  <div className="bot-progress-widget__summary-row bot-progress-widget__summary-row--small">
                    <span>
                      {/* {source?.processed_items || 0}/{source?.total_items || 0} items */}
                    </span>
                    <strong>{progress}%</strong>
                  </div>

                  <div className="bot-progress-widget__bar bot-progress-widget__bar--small">
                    <span style={{ width: `${progress}%` }} />
                  </div>

                  <p className="bot-progress-widget__message">
                    {source?.message || "Waiting for updates..."}
                  </p>

                  {isFailed && source?.ks_id && (
                    <div className="bot-progress-widget__item-actions">
                      <button
                        type="button"
                        className="bot-progress-widget__action-button"
                        onClick={() =>
                          handleSourceAction("POST", source.ks_id, "Retry")
                        }
                        disabled={isActionPending}
                      >
                        {pendingAction === "POST" ? "Retrying..." : "Retry"}
                      </button>
                      <button
                        type="button"
                        className="bot-progress-widget__action-button bot-progress-widget__action-button--danger"
                        onClick={() =>
                          handleSourceAction("DELETE", source.ks_id, "Delete")
                        }
                        disabled={isActionPending}
                      >
                        {pendingAction === "DELETE" ? "Deleting..." : "Delete"}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* {!allCompleted && hasProcessing && (
            <p className="bot-progress-widget__note">
              This notification stays open until processing finishes.
            </p>
          )} */}
        </>
      )}
    </aside>
  );
}
